import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useAuth } from './auth-context';

interface PermissionsContextType {
  permissions: string[];
  loading: boolean;
  refetch: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType>({
  permissions: [],
  loading: true,
  refetch: async () => {},
});

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { authFetch } = await import('./api');
      const res = await authFetch('/api/v1/me/permissions');
      if (res.ok) {
        const data = await res.json();
        setPermissions(data.permissions ?? []);
      } else {
        setPermissions([]);
      }
    } catch {
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(refetch, 60_000);
    return () => clearInterval(interval);
  }, [isAuthenticated, refetch]);

  return (
    <PermissionsContext.Provider value={{ permissions, loading, refetch }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}
